// ============================================
// Watchlist Page - Keyword watchlist with auto-alerts
// ============================================

import { useState, useCallback } from "react";
import {
  WatchlistItem,
  DomainCategory,
  DomainItem,
  BatchAnalysisJob,
} from "../../types";
import { DropCatchService } from "../../services/dropcatchService";
import { BatchAnalysisService } from "../../services/batchAnalysisService";

interface WatchlistProps {
  items: WatchlistItem[];
  categories: DomainCategory[];
  dropcatchService: DropCatchService;
  batchAnalysisService: BatchAnalysisService;
  onItemsChange: (items: WatchlistItem[]) => void;
  onBackorder?: (domain: string, maxBid?: number) => void;
  className?: string;
}

/**
 * Watchlist Component
 *
 * Manages keyword watchlist with:
 * - Keyword management (CRUD)
 * - Auto-backorder option
 * - Max bid settings
 * - Real-time match scanning
 * - Batch analysis of matches
 */
export function Watchlist({
  items,
  categories,
  dropcatchService,
  batchAnalysisService,
  onItemsChange,
  onBackorder,
  className = "",
}: WatchlistProps) {
  // State
  const [_loading, _setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [matches, setMatches] = useState<Map<string, DomainItem[]>>(new Map());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newItem, setNewItem] = useState<Partial<WatchlistItem> | null>(null);
  const [batchJob, setBatchJob] = useState<BatchAnalysisJob | null>(null);
  const [selectedMatches, setSelectedMatches] = useState<Set<string>>(new Set());

  // Generate unique ID
  const generateId = () => {
    return `watch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Scan for matches
  const handleScan = useCallback(async () => {
    setScanning(true);
    const newMatches = new Map<string, DomainItem[]>();

    try {
      for (const item of items.filter((i) => i.enabled)) {
        const results = await dropcatchService.searchAuctions({
          searchTerm: item.keyword,
          size: 50,
          showAllActive: true,
        });

        if (results.items.length > 0) {
          newMatches.set(item.id, results.items);

          // Update match count
          const updatedItems = items.map((i) =>
            i.id === item.id
              ? {
                  ...i,
                  matchCount: results.items.length,
                  lastMatch: new Date().toISOString(),
                }
              : i
          );
          onItemsChange(updatedItems);
        }
      }

      setMatches(newMatches);
    } catch (error) {
      console.error("Scan failed:", error);
    } finally {
      setScanning(false);
    }
  }, [items, dropcatchService, onItemsChange]);

  // Add new watchlist item
  const handleAddItem = () => {
    setNewItem({
      id: generateId(),
      keyword: "",
      categoryId: categories[0]?.id || "",
      autoBackorder: false,
      maxBid: null,
      enabled: true,
      matchCount: 0,
      createdAt: new Date().toISOString(),
      lastMatch: null,
    });
    setEditingId(null);
  };

  // Save new item
  const handleSaveNew = () => {
    if (!newItem || !newItem.keyword?.trim()) return;

    const item: WatchlistItem = {
      id: newItem.id || generateId(),
      keyword: newItem.keyword.trim().toLowerCase(),
      categoryId: newItem.categoryId || categories[0]?.id || "",
      autoBackorder: newItem.autoBackorder || false,
      maxBid: newItem.maxBid || null,
      enabled: newItem.enabled !== false,
      matchCount: 0,
      createdAt: newItem.createdAt || new Date().toISOString(),
      lastMatch: null,
    };

    onItemsChange([...items, item]);
    setNewItem(null);
  };

  // Update item
  const handleUpdateItem = (itemId: string, updates: Partial<WatchlistItem>) => {
    const updatedItems = items.map((i) =>
      i.id === itemId ? { ...i, ...updates } : i
    );
    onItemsChange(updatedItems);
    setEditingId(null);
  };

  // Delete item
  const handleDeleteItem = (itemId: string) => {
    onItemsChange(items.filter((i) => i.id !== itemId));
    setMatches((prev) => {
      const newMap = new Map(prev);
      newMap.delete(itemId);
      return newMap;
    });
  };

  // Toggle item enabled
  const handleToggleEnabled = (itemId: string) => {
    const item = items.find((i) => i.id === itemId);
    if (item) {
      handleUpdateItem(itemId, { enabled: !item.enabled });
    }
  };

  // Batch analyze selected matches
  const handleBatchAnalyze = () => {
    const domainsToAnalyze: string[] = [];
    matches.forEach((domainList) => {
      domainList.forEach((d) => {
        if (selectedMatches.has(d.id) || selectedMatches.size === 0) {
          domainsToAnalyze.push(d.name);
        }
      });
    });

    if (domainsToAnalyze.length === 0) return;

    const job = batchAnalysisService.createJob(domainsToAnalyze);
    setBatchJob(job);

    // Subscribe to updates
    batchAnalysisService.subscribe(job.id, (updatedJob) => {
      setBatchJob({ ...updatedJob });
    });
  };

  // Backorder selected matches
  const handleBackorderSelected = () => {
    if (!onBackorder) return;

    matches.forEach((domainList) => {
      domainList.forEach((d) => {
        if (selectedMatches.has(d.id)) {
          const watchItem = items.find((i) => i.id === d.id);
          onBackorder(d.name, watchItem?.maxBid || undefined);
        }
      });
    });

    setSelectedMatches(new Set());
  };

  // Toggle match selection
  const toggleMatchSelection = (domainId: string) => {
    setSelectedMatches((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(domainId)) {
        newSet.delete(domainId);
      } else {
        newSet.add(domainId);
      }
      return newSet;
    });
  };

  // Get total matches
  const totalMatches = Array.from(matches.values()).reduce(
    (sum, list) => sum + list.length,
    0
  );

  return (
    <div className={`flex flex-col h-full bg-black ${className}`}>
      {/* Header */}
      <div className="px-6 py-3 border-b-2 border-green-500 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 animate-pulse" />
            <span className="text-green-400 font-bold tracking-wider">
              KEYWORD WATCHLIST
            </span>
          </div>
          <span className="text-xs text-green-600">
            [{items.length} KEYWORDS] [{totalMatches} MATCHES]
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleScan}
            disabled={scanning || items.length === 0}
            className="px-3 py-1 border border-green-500 text-green-500 text-xs hover:bg-green-500 hover:text-black disabled:opacity-50"
          >
            {scanning ? "[SCANNING...]" : "[SCAN NOW]"}
          </button>
          <button
            onClick={handleAddItem}
            disabled={!!newItem}
            className="px-3 py-1 bg-green-500 text-black font-bold text-xs hover:bg-green-400 disabled:opacity-50"
          >
            [+ ADD KEYWORD]
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
          {/* Watchlist Items */}
          <div className="border-2 border-green-700">
            <div className="px-4 py-2 border-b border-green-700 bg-green-900/20">
              <span className="text-sm text-green-400 font-bold tracking-wider">
                WATCHED KEYWORDS
              </span>
            </div>
            <div className="p-4 space-y-3">
              {/* New Item Form */}
              {newItem && (
                <WatchlistItemForm
                  item={newItem}
                  categories={categories}
                  onSave={handleSaveNew}
                  onCancel={() => setNewItem(null)}
                  onChange={(updates) => setNewItem({ ...newItem, ...updates })}
                  isNew
                />
              )}

              {/* Existing Items */}
              {items.map((item) => (
                <div key={item.id}>
                  {editingId === item.id ? (
                    <WatchlistItemForm
                      item={item}
                      categories={categories}
                      onSave={() => setEditingId(null)}
                      onCancel={() => setEditingId(null)}
                      onChange={(updates) => handleUpdateItem(item.id, updates)}
                    />
                  ) : (
                    <WatchlistItemCard
                      item={item}
                      category={categories.find((c) => c.id === item.categoryId)}
                      matchCount={matches.get(item.id)?.length || 0}
                      onEdit={() => setEditingId(item.id)}
                      onDelete={() => handleDeleteItem(item.id)}
                      onToggleEnabled={() => handleToggleEnabled(item.id)}
                    />
                  )}
                </div>
              ))}

              {/* Empty State */}
              {items.length === 0 && !newItem && (
                <div className="text-center py-8 text-green-600">
                  NO KEYWORDS CONFIGURED
                  <div className="mt-2 text-xs">
                    Add keywords to watch for matching domains
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Matches Panel */}
          <div className="border-2 border-green-700">
            <div className="px-4 py-2 border-b border-green-700 bg-green-900/20 flex items-center justify-between">
              <span className="text-sm text-green-400 font-bold tracking-wider">
                MATCHING DOMAINS
              </span>
              {totalMatches > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleBatchAnalyze}
                    disabled={!!batchJob && batchJob.status === "processing"}
                    className="px-2 py-1 border border-green-700 text-green-600 text-xs hover:border-green-500"
                  >
                    [ANALYZE ALL]
                  </button>
                  {onBackorder && selectedMatches.size > 0 && (
                    <button
                      onClick={handleBackorderSelected}
                      className="px-2 py-1 bg-green-500 text-black text-xs font-bold"
                    >
                      [BACKORDER ({selectedMatches.size})]
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Batch Job Progress */}
            {batchJob && batchJob.status === "processing" && (
              <div className="px-4 py-2 bg-green-900/30 border-b border-green-800">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-green-500">ANALYZING...</span>
                  <span className="text-xs text-green-400">{batchJob.progress}%</span>
                </div>
                <div className="w-full bg-green-900 h-2">
                  <div
                    className="bg-green-500 h-2 transition-all"
                    style={{ width: `${batchJob.progress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Analysis Results Summary - shows when complete */}
            {batchJob && batchJob.status === "completed" && (
              <div className="px-4 py-3 bg-green-900/20 border-b border-green-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-green-400 font-bold">ANALYSIS COMPLETE</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        const csv = batchAnalysisService.exportResultsAsCSV(batchJob.id);
                        const blob = new Blob([csv], { type: 'text/csv' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `watchlist-analysis-${new Date().toISOString().split('T')[0]}.csv`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      className="px-2 py-1 border border-green-500 text-green-500 text-xs hover:bg-green-500 hover:text-black"
                    >
                      [EXPORT CSV]
                    </button>
                    <button
                      onClick={() => setBatchJob(null)}
                      className="px-2 py-1 border border-green-700 text-green-600 text-xs hover:border-green-500"
                    >
                      [CLEAR]
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="p-2 border border-green-700 text-center">
                    <div className="text-green-400 font-bold text-lg">
                      {Object.values(batchJob.results).filter(r => r.verdict === "Top Pick").length}
                    </div>
                    <div className="text-green-600">TOP PICKS</div>
                  </div>
                  <div className="p-2 border border-yellow-700 text-center">
                    <div className="text-yellow-400 font-bold text-lg">
                      {Object.values(batchJob.results).filter(r => r.verdict === "Backorder" || r.verdict === "Hand Register").length}
                    </div>
                    <div className="text-yellow-600">CONSIDER</div>
                  </div>
                  <div className="p-2 border border-red-700 text-center">
                    <div className="text-red-400 font-bold text-lg">
                      {Object.values(batchJob.results).filter(r => r.verdict === "Pass").length}
                    </div>
                    <div className="text-red-600">PASS</div>
                  </div>
                </div>
                {/* Top Picks List */}
                {Object.entries(batchJob.results).filter(([, r]) => r.verdict === "Top Pick").length > 0 && (
                  <div className="mt-3 p-2 border border-green-500 bg-green-900/20">
                    <div className="text-xs text-green-400 font-bold mb-2">TOP PICKS:</div>
                    <div className="space-y-1">
                      {Object.entries(batchJob.results)
                        .filter(([, r]) => r.verdict === "Top Pick")
                        .map(([domain, result]) => (
                          <div key={domain} className="flex items-center justify-between text-xs">
                            <span className="text-green-300 font-mono">{domain}</span>
                            <span className="text-green-500">{result.valuation}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="p-4 space-y-4 max-h-[600px] overflow-y-auto">
              {Array.from(matches.entries()).map(([itemId, domainList]) => {
                const watchItem = items.find((i) => i.id === itemId);
                if (!watchItem) return null;

                return (
                  <div key={itemId} className="space-y-2">
                    <div className="text-xs text-green-600 tracking-wider flex items-center gap-2">
                      <span className="text-green-500 font-bold">
                        "{watchItem.keyword.toUpperCase()}"
                      </span>
                      <span>({domainList.length} matches)</span>
                    </div>
                    {domainList.slice(0, 10).map((domain) => (
                      <MatchCard
                        key={domain.id}
                        domain={domain}
                        selected={selectedMatches.has(domain.id)}
                        analysisResult={batchJob?.results[domain.name]}
                        onSelect={() => toggleMatchSelection(domain.id)}
                        onBackorder={
                          onBackorder
                            ? () => onBackorder(domain.name, watchItem.maxBid || undefined)
                            : undefined
                        }
                      />
                    ))}
                    {domainList.length > 10 && (
                      <div className="text-xs text-green-700 text-center">
                        +{domainList.length - 10} more matches
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Empty State */}
              {totalMatches === 0 && (
                <div className="text-center py-8 text-green-600">
                  {scanning ? (
                    <span className="animate-pulse">SCANNING FOR MATCHES...</span>
                  ) : (
                    <>
                      NO MATCHES FOUND
                      <div className="mt-2 text-xs">
                        Click "SCAN NOW" to search for matching domains
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Watchlist Item Card
 */
function WatchlistItemCard({
  item,
  category,
  matchCount,
  onEdit,
  onDelete,
  onToggleEnabled,
}: {
  item: WatchlistItem;
  category?: DomainCategory;
  matchCount: number;
  onEdit: () => void;
  onDelete: () => void;
  onToggleEnabled: () => void;
}) {
  return (
    <div
      className={`border p-3 transition-colors ${
        item.enabled
          ? "border-green-700 hover:border-green-500"
          : "border-green-900 opacity-50"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <button
            onClick={onToggleEnabled}
            className={`w-4 h-4 border ${
              item.enabled
                ? "border-green-500 bg-green-500"
                : "border-green-700"
            }`}
          >
            {item.enabled && <span className="text-black text-xs">✓</span>}
          </button>
          <span className="text-green-400 font-bold tracking-wider">
            {item.keyword.toUpperCase()}
          </span>
          {matchCount > 0 && (
            <span className="px-2 py-0.5 bg-green-500 text-black text-xs font-bold">
              {matchCount} MATCHES
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onEdit}
            className="px-2 py-1 border border-green-700 text-green-600 text-xs hover:border-green-500"
          >
            [EDIT]
          </button>
          <button
            onClick={onDelete}
            className="px-2 py-1 border border-red-800 text-red-600 text-xs hover:border-red-500"
          >
            [DEL]
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4 text-xs text-green-600">
        {category && (
          <span className="flex items-center gap-1">
            <div
              className="w-2 h-2"
              style={{ backgroundColor: category.color }}
            />
            {category.name}
          </span>
        )}
        {item.autoBackorder && (
          <span className="text-yellow-500">AUTO-BACKORDER</span>
        )}
        {item.maxBid && <span>MAX: ${item.maxBid}</span>}
        {item.lastMatch && (
          <span>
            Last match: {new Date(item.lastMatch).toLocaleDateString()}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Watchlist Item Form
 */
function WatchlistItemForm({
  item,
  categories,
  onSave,
  onCancel,
  onChange,
  isNew = false,
}: {
  item: Partial<WatchlistItem>;
  categories: DomainCategory[];
  onSave: () => void;
  onCancel: () => void;
  onChange: (updates: Partial<WatchlistItem>) => void;
  isNew?: boolean;
}) {
  return (
    <div className="border-2 border-green-500 p-4 bg-green-900/20">
      <div className="flex items-center justify-between mb-4">
        <span className="text-green-400 font-bold tracking-wider">
          {isNew ? "NEW KEYWORD" : "EDIT KEYWORD"}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={onSave}
            disabled={!item.keyword?.trim()}
            className="px-3 py-1 bg-green-500 text-black font-bold text-xs hover:bg-green-400 disabled:opacity-50"
          >
            [SAVE]
          </button>
          <button
            onClick={onCancel}
            className="px-3 py-1 border border-green-700 text-green-600 text-xs"
          >
            [CANCEL]
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Keyword */}
        <div>
          <label className="block text-xs text-green-600 mb-1">KEYWORD</label>
          <input
            type="text"
            value={item.keyword || ""}
            onChange={(e) => onChange({ keyword: e.target.value })}
            placeholder="Enter keyword..."
            className="w-full px-3 py-2 bg-black border border-green-700 text-green-400 focus:border-green-500 focus:outline-none placeholder-green-800 text-sm"
          />
        </div>

        {/* Category */}
        <div>
          <label className="block text-xs text-green-600 mb-1">CATEGORY</label>
          <select
            value={item.categoryId || ""}
            onChange={(e) => onChange({ categoryId: e.target.value })}
            className="w-full px-3 py-2 bg-black border border-green-700 text-green-400 focus:border-green-500 focus:outline-none text-sm"
          >
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        {/* Max Bid */}
        <div>
          <label className="block text-xs text-green-600 mb-1">MAX BID ($)</label>
          <input
            type="number"
            value={item.maxBid || ""}
            onChange={(e) =>
              onChange({
                maxBid: e.target.value ? Number(e.target.value) : null,
              })
            }
            placeholder="No limit"
            className="w-full px-3 py-2 bg-black border border-green-700 text-green-400 focus:border-green-500 focus:outline-none placeholder-green-800 text-sm"
          />
        </div>

        {/* Auto-backorder */}
        <div className="flex items-center">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={item.autoBackorder || false}
              onChange={(e) => onChange({ autoBackorder: e.target.checked })}
              className="w-4 h-4 accent-green-500"
            />
            <span className="text-xs text-green-500">AUTO-BACKORDER</span>
          </label>
        </div>
      </div>
    </div>
  );
}

/**
 * Match Card
 */
function MatchCard({
  domain,
  selected,
  analysisResult,
  onSelect,
  onBackorder,
}: {
  domain: DomainItem;
  selected: boolean;
  analysisResult?: any;
  onSelect: () => void;
  onBackorder?: () => void;
}) {
  const getVerdictColor = (verdict?: string) => {
    if (!verdict) return "text-green-600";
    if (verdict === "Top Pick") return "text-green-400";
    if (verdict === "Backorder" || verdict === "Consider") return "text-yellow-400";
    return "text-red-400";
  };

  return (
    <div
      className={`border p-2 transition-colors cursor-pointer ${
        selected
          ? "border-green-500 bg-green-900/30"
          : "border-green-800 hover:border-green-600"
      }`}
      onClick={onSelect}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div
            className={`w-3 h-3 border ${
              selected ? "border-green-500 bg-green-500" : "border-green-700"
            }`}
          />
          <span className="text-green-400 font-mono text-sm">{domain.name}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-green-600">${domain.price}</span>
          {analysisResult && (
            <span className={`text-xs font-bold ${getVerdictColor(analysisResult.verdict)}`}>
              [{analysisResult.verdict?.toUpperCase()}]
            </span>
          )}
          {onBackorder && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onBackorder();
              }}
              className="px-2 py-0.5 border border-green-700 text-green-600 text-xs hover:border-green-500 hover:text-green-400"
            >
              [BO]
            </button>
          )}
        </div>
      </div>
      {analysisResult?.valuation && (
        <div className="mt-1 text-xs text-green-700 truncate">
          Est: {analysisResult.valuation}
        </div>
      )}
    </div>
  );
}

export default Watchlist;
